import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import api, { primeCsrfToken } from "./api/api";
import { ThemeProvider } from "./context/ThemeContext";
import { ToastProvider } from "./context/ToastContext";
import Layout from "./layout/Layout";
import Login from "./pages/Login";
import { clearAllCachedValues, isAbortError, runTrackedRequest } from "./utils/cache";
import { inGroup } from "./utils/roles";
import { canRaiseServiceCall } from "./components/service/serviceDeskMeta";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const CoordinatorDashboard = lazy(() => import("./pages/CoordinatorDashboard"));
const EmployeeDashboard = lazy(() => import("./pages/EmployeeDashboard"));
const CRMDashboard = lazy(() => import("./pages/CRMDashboard"));
const Tasks = lazy(() => import("./pages/Tasks"));
const ServiceCalls = lazy(() => import("./pages/ServiceCalls"));
const Documents = lazy(() => import("./pages/Documents"));
const Expenses = lazy(() => import("./pages/Expenses"));
const ExpenseVoucherForm = lazy(() => import("./pages/ExpenseVoucherForm"));
const ExpenseVoucherDetail = lazy(() => import("./pages/ExpenseVoucherDetail"));
const SharedVoucher = lazy(() => import("./pages/SharedVoucher"));
const Approvals = lazy(() => import("./pages/Approvals"));
const CRM = lazy(() => import("./pages/CRM"));
const Users = lazy(() => import("./pages/Users"));
const EmployeePerformance = lazy(() => import("./pages/EmployeePerformance"));
const Profile = lazy(() => import("./pages/Profile"));
const WorkReportsPage = lazy(() => import("./pages/WorkReportsPage"));
const Leave = lazy(() => import("./pages/Leave"));
const PurchaseOrders = lazy(() => import("./pages/PurchaseOrders"));
const CreatePO = lazy(() => import("./pages/CreatePO"));
const ViewPO = lazy(() => import("./pages/ViewPO"));
const DepartmentsPage = lazy(() => import("./pages/DepartmentsPage"));
const Settings = lazy(() => import("./pages/Settings"));
const HelpSupport = lazy(() => import("./pages/HelpSupport"));
const TVDashboard = lazy(() => import("./pages/TVDashboard"));
const LiveMap = lazy(() => import("./pages/LiveMap"));
const FaceAttendance = lazy(() => import("./pages/FaceAttendance"));
const AttendanceDashboard = lazy(() => import("./pages/attendance/AttendanceDashboard"));
const AttendanceToday = lazy(() => import("./pages/attendance/AttendanceToday"));
const AttendanceEmployees = lazy(() => import("./pages/attendance/AttendanceEmployees"));
const AttendanceManualCorrection = lazy(() => import("./pages/attendance/AttendanceManualCorrection"));
const AttendanceReports = lazy(() => import("./pages/attendance/AttendanceReports"));
const MyAttendance = lazy(() => import("./pages/attendance/MyAttendance"));

const DEPT_ROLES = new Set(["admin", "super_admin"]);
// Admin-only while face attendance is in the testing phase — widen this once
// the rollout moves on to other roles/departments.
const FACE_ATTENDANCE_ROLES = new Set(["super_admin"]);
const ATTENDANCE_ADMIN_ROLES = new Set(["super_admin"]);
const LIVE_MAP_ROLES = new Set(["admin", "super_admin", "supervisor", "coordinator", "service_coordinator"]);
const PO_ROLES = new Set(["finance", "finance_officer", "admin", "super_admin", "supervisor", "coordinator", "service_coordinator", "employee"]);
// Finance can view/approve POs but no longer creates or edits them — see ViewPO.jsx.
const PO_CREATE_ROLES = new Set(["admin", "super_admin"]);
const APPROVAL_ROLES = new Set(["admin", "super_admin", "supervisor", "finance", "finance_officer", "coordinator", "service_coordinator"]);
// supervisor added here (was previously only allowed onto the now-removed
// standalone /crm/sheets page): backend already grants supervisor "full"
// access to the crm module (see PERMISSIONS["crm"] in
// app/core/permissions.py) — this frontend gate had drifted out of sync
// with it, silently locking supervisors out of the merged CRM page.
const CRM_ROLES = new Set(["admin", "super_admin", "crm", "supervisor"]);
const USER_ADMIN_ROLES = ["admin", "super_admin", "supervisor", "finance"];
// Mirrors EMPLOYEE_PERFORMANCE_ROLES in backend/app/modules/users/router.py —
// employee-wide KPI details are restricted to the same roles that already see
// employee results in global search.
const EMPLOYEE_PERFORMANCE_ROLES = new Set(["admin", "super_admin", "supervisor"]);
const TV_ROLES = new Set(["admin", "super_admin", "tv_monitor"]);
const DASHBOARD_ROUTE = "/dashboard";

function ServiceDepartmentRedirect() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/service-calls", search }} replace />;
}

function PageFallback() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-28 rounded-3xl bg-white shadow-sm" />
      <div className="grid gap-4 md:grid-cols-2">
        <div className="h-52 rounded-3xl bg-white shadow-sm" />
        <div className="h-52 rounded-3xl bg-white shadow-sm" />
      </div>
    </div>
  );
}

function DashboardStartupRedirect({ enabled }) {
  const location = useLocation();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (!enabled || hasRedirected.current) return;

    hasRedirected.current = true;
    if (location.pathname !== DASHBOARD_ROUTE) {
      navigate(DASHBOARD_ROUTE, { replace: true });
    }
  }, [enabled, location.pathname, navigate]);

  return null;
}

function AuthenticatedShell({ role, onLogout, restoreDashboardOnMount }) {
  return (
    <Layout role={role} onLogout={onLogout}>
      <DashboardStartupRedirect enabled={restoreDashboardOnMount} />
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
    </Layout>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [restoreDashboardOnMount, setRestoreDashboardOnMount] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    runTrackedRequest({
      path: "/auth/me",
      source: "AppSession",
      signal: controller.signal,
      request: ({ signal }) => api.get("/auth/me", { signal }),
    })
      .then((res) => {
        const data = res.data;
        const nextRole = data.role || data.roles?.[0] || "employee";
        setUser(data);
        setRole(nextRole);
        setRestoreDashboardOnMount(true);
        localStorage.setItem("role", nextRole);
        localStorage.setItem("user", JSON.stringify(data));
        void primeCsrfToken();
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }

        localStorage.removeItem("user");
        localStorage.removeItem("role");
        localStorage.removeItem("token");
        clearAllCachedValues();
        setRestoreDashboardOnMount(false);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setSessionLoading(false);
        }
      });

    return () => controller.abort();
  }, []);

  const handleLogin = (userData, nextRole) => {
    clearAllCachedValues(); // belt and braces: start every sign-in with an empty cache
    setRestoreDashboardOnMount(false);
    setUser(userData);
    setRole(nextRole);
  };

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("user");
    localStorage.removeItem("role");
    localStorage.removeItem("token");
    clearAllCachedValues(); // nothing from this account may outlive the session
    setRestoreDashboardOnMount(false);
    setUser(null);
    setRole(null);
  };

  if (sessionLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "#f8fafc",
          color: "#64748b",
          fontSize: 14,
          fontFamily: "system-ui, sans-serif",
        }}
      >
        Loading...
      </div>
    );
  }

  const isEmployee = role === "employee";
  const isCRM = role === "crm";
  const isTVMonitor = role === "tv_monitor";
  const isCoordinator = inGroup(role, "COORDINATOR");
  const homeRoute = isTVMonitor ? "/tv-dashboard" : DASHBOARD_ROUTE;
  const dashboardElement = isTVMonitor
    ? <TVDashboard />
    : isCRM
      ? <CRMDashboard />
      : isEmployee
        ? <EmployeeDashboard />
        : isCoordinator
          ? <CoordinatorDashboard />
          : <Dashboard />;

  return (
    <ThemeProvider>
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            {!user ? (
              <>
                <Route path="/login" element={<Login onLogin={handleLogin} />} />
                <Route path="/loging" element={<Navigate to="/login" replace />} />
                <Route
                  path="/expenses/shared/:token"
                  element={
                    <Suspense fallback={<PageFallback />}>
                      <SharedVoucher />
                    </Suspense>
                  }
                />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            ) : (
              <>
                <Route path="/login" element={<Navigate to={homeRoute} replace />} />
                <Route path="/loging" element={<Navigate to={homeRoute} replace />} />
                <Route
                  path="/expenses/shared/:token"
                  element={
                    <Suspense fallback={<PageFallback />}>
                      <SharedVoucher />
                    </Suspense>
                  }
                />
                <Route
                  element={
                    <AuthenticatedShell
                      role={role}
                      onLogout={handleLogout}
                      restoreDashboardOnMount={restoreDashboardOnMount}
                    />
                  }
                >
                  <Route path="/" element={<Navigate to={homeRoute} replace />} />
                  <Route path="/dashboard" element={isTVMonitor ? <Navigate to="/tv-dashboard" replace /> : dashboardElement} />
                  <Route path="/tasks" element={isTVMonitor ? <Navigate to="/tv-dashboard" replace /> : <Tasks />} />
                  <Route
                    path="/service-calls"
                    element={!isTVMonitor && canRaiseServiceCall(role) ? <ServiceCalls /> : <Navigate to={homeRoute} replace />}
                  />
                  {/* Service Department was merged into Service Calls; keep old links and notifications working. */}
                  <Route path="/service-department" element={<ServiceDepartmentRedirect />} />
                  <Route path="/expenses" element={isTVMonitor ? <Navigate to="/tv-dashboard" replace /> : <Expenses />} />
                  <Route path="/expenses/new" element={isTVMonitor ? <Navigate to="/tv-dashboard" replace /> : <ExpenseVoucherForm />} />
                  <Route path="/expenses/:id/edit" element={isTVMonitor ? <Navigate to="/tv-dashboard" replace /> : <ExpenseVoucherForm />} />
                  <Route path="/expenses/:id" element={isTVMonitor ? <Navigate to="/tv-dashboard" replace /> : <ExpenseVoucherDetail />} />
                  <Route
                    path="/approvals"
                    element={!isTVMonitor && APPROVAL_ROLES.has(role) ? <Approvals /> : <Navigate to={homeRoute} replace />}
                  />
                  <Route path="/documents" element={isTVMonitor ? <Navigate to="/tv-dashboard" replace /> : <Documents />} />
                  <Route path="/work-reports" element={isTVMonitor ? <Navigate to="/tv-dashboard" replace /> : <WorkReportsPage />} />
                  <Route path="/report" element={<Navigate to={isTVMonitor ? "/tv-dashboard" : "/work-reports"} replace />} />
                  <Route path="/leave" element={isTVMonitor ? <Navigate to="/tv-dashboard" replace /> : <Leave />} />
                  <Route
                    path="/crm"
                    element={CRM_ROLES.has(role) ? <CRM /> : <Navigate to={homeRoute} replace />}
                  />
                  {/* Daily Call Sheets merged into the CRM page's own tab — old
                      links/bookmarks to the standalone page still land somewhere useful. */}
                  <Route path="/crm/sheets" element={<Navigate to="/crm?tab=sheets" replace />} />
                  <Route
                    path="/users"
                    element={USER_ADMIN_ROLES.includes(role) ? <Users /> : <Navigate to={homeRoute} replace />}
                  />
                  <Route
                    path="/admin/employees/:employeeId/performance"
                    element={EMPLOYEE_PERFORMANCE_ROLES.has(role) ? <EmployeePerformance /> : <Navigate to={homeRoute} replace />}
                  />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/settings" element={<Settings onLogout={handleLogout} />} />
                  <Route path="/help" element={<HelpSupport />} />
                  <Route
                    path="/departments"
                    element={DEPT_ROLES.has(role) ? <DepartmentsPage /> : <Navigate to={homeRoute} replace />}
                  />
                  <Route
                    path="/purchase-orders"
                    element={PO_ROLES.has(role) ? <PurchaseOrders /> : <Navigate to={homeRoute} replace />}
                  />
                  <Route
                    path="/purchase-orders/new"
                    element={PO_CREATE_ROLES.has(role) ? <CreatePO /> : <Navigate to={homeRoute} replace />}
                  />
                  <Route
                    path="/purchase-orders/:id"
                    element={PO_ROLES.has(role) ? <ViewPO /> : <Navigate to={homeRoute} replace />}
                  />
                  <Route
                    path="/live-map"
                    element={!isTVMonitor && LIVE_MAP_ROLES.has(role) ? <LiveMap /> : <Navigate to={homeRoute} replace />}
                  />
                  <Route
                    path="/face-attendance"
                    element={!isTVMonitor && FACE_ATTENDANCE_ROLES.has(role) ? <FaceAttendance /> : <Navigate to={homeRoute} replace />}
                  />
                  <Route path="/attendance" element={!isTVMonitor && ATTENDANCE_ADMIN_ROLES.has(role) ? <AttendanceDashboard /> : <Navigate to={homeRoute} replace />} />
                  <Route path="/attendance/today" element={!isTVMonitor && ATTENDANCE_ADMIN_ROLES.has(role) ? <AttendanceToday /> : <Navigate to={homeRoute} replace />} />
                  <Route path="/attendance/employees" element={!isTVMonitor && ATTENDANCE_ADMIN_ROLES.has(role) ? <AttendanceEmployees /> : <Navigate to={homeRoute} replace />} />
                  <Route path="/attendance/manual" element={!isTVMonitor && ATTENDANCE_ADMIN_ROLES.has(role) ? <AttendanceManualCorrection /> : <Navigate to={homeRoute} replace />} />
                  <Route path="/attendance/reports" element={!isTVMonitor && ATTENDANCE_ADMIN_ROLES.has(role) ? <AttendanceReports /> : <Navigate to={homeRoute} replace />} />
                  <Route path="/my-attendance" element={!isTVMonitor ? <MyAttendance /> : <Navigate to={homeRoute} replace />} />
                </Route>
                <Route
                  path="/tv-dashboard"
                  element={
                    TV_ROLES.has(role) ? (
                      <Suspense fallback={<PageFallback />}>
                        <TVDashboard />
                      </Suspense>
                    ) : (
                      <Navigate to={homeRoute} replace />
                    )
                  }
                />
                <Route path="*" element={<Navigate to={homeRoute} replace />} />
              </>
            )}
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </ThemeProvider>
  );
}
